<template>
  <div class="gs-asset-type">
    <asset-segmented
      :items="signaturesTabs"
      :current="signaturesTab"
      :error-values="signaturesErrorTabs"
      label="Signature types"
      @select="handleSignaturesTabChange"
    />

    <div v-if="signaturesTab === 'email'" class="gs-asset-split">
      <template v-for="(post, index) in emailSignatures">
        <section
          v-if="isEmailSignatureTabActive(post)"
          :key="post.id"
          class="gs-asset-editor"
        >
          <header class="gs-asset-editor__head">
            <div class="gs-asset-editor__head-fields">
              <gs-field :label="$t('frontend_resources_name')">
                <text-input
                  v-model="post.name"
                  :validator="$v.localFunnelResource.signatures.email.$each[index].name"
                  :attribute="$t('frontend_groovesell_signature_name')"
                  :placeholder="$t('frontend_resources_name')"
                />
              </gs-field>
            </div>
            <div class="gs-asset-editor__controls">
              <span class="gs-asset-editor__toggle">
                <span>Show to affiliates</span>
                <gd-toggle v-model="post.enabled" :value="post.enabled" />
              </span>
              <button
                type="button"
                class="gs-asset-editor__delete"
                data-action="delete-email-signature"
                title="Delete email signature"
                aria-label="Delete email signature"
                @click="openConfirmModal(index, 'deleteEmailSignature')"
              >
                <trash-icon class="w-4 h-4 fill-current" />
              </button>
            </div>
          </header>

          <div class="gs-asset-editor__section">
            <field-error
              :validator="$v.localFunnelResource.signatures.email.$each[index].post"
              :attribute="$t('frontend_signature')"
            >
              <template #default="{ hasErrors, message }">
                <gs-field :label="$t('frontend_resources_signature')" :error="message">
                  <textarea
                    v-model="post.post"
                    class="gs-textarea gs-textarea--short"
                    :class="{ 'has-error': hasErrors }"
                    :placeholder="$t('frontend_resources_signature')"
                  ></textarea>
                </gs-field>
              </template>
            </field-error>
            <div class="gs-asset-editor__foot">
              <span class="gs-asset-editor__count">{{ characterCount(post.post) }}</span>
              <gs-name-select placeholder="Insert" max-height="min(280px, 40vh)" :actions="emailSignatureInsertActions(index)" size="action" />
            </div>
          </div>
        </section>
      </template>

      <!-- Only shown when there is something to pick: with an empty list the
           list's own empty state is the single message. -->
      <div v-if="hasEmailSignatures && !selectedEmailSignature" class="gs-asset-empty">
        <p>Select an email signature from the list to edit it.</p>
      </div>
      <asset-item-list
        title="Email signatures"
        :items="emailSignatures"
        :selected-id="selectedEmailSignatureId"
        :add-label="$t('frontend_resources_add_new')"
        name-fallback="Untitled signature"
        :empty-copy="$t('frontend_resources_you_currently_dont_have_any_email_signatures')"
        enabled-key="enabled"
        :error-ids="emailSignaturesErrorIds"
        @select="selectEmailSignature"
        @add="createEmailSignature()"
        @reorder="reorderEmailSignatures"
      />
    </div>

    <div v-else-if="signaturesTab === 'forum'" class="gs-asset-split">
      <template v-for="(post, index) in forumSignatures">
        <section
          v-if="isForumSignatureTabActive(post)"
          :key="post.id"
          class="gs-asset-editor"
        >
          <header class="gs-asset-editor__head">
            <div class="gs-asset-editor__head-fields">
              <gs-field :label="$t('frontend_resources_name')">
                <text-input
                  v-model="post.name"
                  :validator="$v.localFunnelResource.signatures.forum.$each[index].name"
                  :attribute="$t('frontend_groovesell_signature_name')"
                  :placeholder="$t('frontend_resources_name')"
                />
              </gs-field>
            </div>
            <div class="gs-asset-editor__controls">
              <span class="gs-asset-editor__toggle">
                <span>Show to affiliates</span>
                <gd-toggle v-model="post.enabled" :value="post.enabled" />
              </span>
              <button
                type="button"
                class="gs-asset-editor__delete"
                data-action="delete-forum-signature"
                title="Delete forum signature"
                aria-label="Delete forum signature"
                @click="openConfirmModal(index, 'deleteForumSignature')"
              >
                <trash-icon class="w-4 h-4 fill-current" />
              </button>
            </div>
          </header>

          <div class="gs-asset-editor__section">
            <field-error
              :validator="$v.localFunnelResource.signatures.forum.$each[index].post"
              :attribute="$t('frontend_signature')"
            >
              <template #default="{ hasErrors, message }">
                <gs-field :label="$t('frontend_resources_signature')" :error="message">
                  <textarea
                    v-model="post.post"
                    class="gs-textarea gs-textarea--short"
                    :class="{ 'has-error': hasErrors }"
                    :placeholder="$t('frontend_resources_signature')"
                  ></textarea>
                </gs-field>
              </template>
            </field-error>
            <div class="gs-asset-editor__foot">
              <span class="gs-asset-editor__count">{{ characterCount(post.post) }}</span>
              <gs-name-select placeholder="Insert" max-height="min(280px, 40vh)" :actions="forumSignatureInsertActions(index)" size="action" />
            </div>
          </div>
        </section>
      </template>

      <div v-if="hasForumSignatures && !selectedForumSignature" class="gs-asset-empty">
        <p>Select a forum signature from the list to edit it.</p>
      </div>
      <asset-item-list
        title="Forum signatures"
        :items="forumSignatures"
        :selected-id="selectedForumSignatureId"
        :add-label="$t('frontend_resources_add_new')"
        name-fallback="Untitled signature"
        :empty-copy="$t('frontend_resources_you_currently_dont_have_any_forum')"
        enabled-key="enabled"
        :error-ids="forumSignaturesErrorIds"
        @select="selectForumSignature"
        @add="createForumSignature()"
        @reorder="reorderForumSignatures"
      />
    </div>

    <gs-alert-dialog
      v-model="confirmOpen"
      title="Delete this signature?"
      :copy="$t('frontend_non_revertable')"
      :confirm-label="$t('frontend_delete_confirm')"
      @confirm="applyAction()"
    />
  </div>
</template>

<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import generateNumberMixin from '@/mixins/generateNumberMixin';
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import AssetItemList from './AssetItemList.vue';
import AssetSegmented from './AssetSegmented.vue';
import FieldError from './FieldError.vue';
import { toTabOrder, sortByTabOrder, idFromTabValue } from './tabOrder';

export default {
  components: {
    AssetItemList,
    AssetSegmented,
    FieldError,
    TrashIcon,
  },
  mixins: [generateNumberMixin],
  props: {
    funnelResource: {
      type: Object,
      required: true,
    },
    landingPages: {
      type: Array,
      required: true,
    },
    emailSignaturesTab: {
      type: String,
      default: '',
    },
    forumSignaturesTab: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      itemToDeleteIndex: null,
      actionType: '',
      confirmOpen: false,
      localFunnelResource: this.funnelResource,
      localEmailSignaturesTab: this.emailSignaturesTab,
      localForumSignaturesTab: this.forumSignaturesTab,
      signaturesTabs: [
        { title: this.$t('frontend_resources_email'), value: 'email' },
        { title: this.$t('frontend_resources_forum'), value: 'forum' },
      ],
      signaturesTab: 'email',
    };
  },
  computed: {
    emailSignatures() {
      return (
        (this.localFunnelResource
          && this.localFunnelResource.signatures
          && this.localFunnelResource.signatures.email) || []
      );
    },
    forumSignatures() {
      return (
        (this.localFunnelResource
          && this.localFunnelResource.signatures
          && this.localFunnelResource.signatures.forum) || []
      );
    },
    hasEmailSignatures() {
      return (
        this.localFunnelResource &&
        this.localFunnelResource.signatures &&
        this.localFunnelResource.signatures.email.length > 0
      );
    },
    hasForumSignatures() {
      return (
        this.localFunnelResource &&
        this.localFunnelResource.signatures &&
        this.localFunnelResource.signatures.forum.length > 0
      );
    },
    selectedEmailSignature() {
      return this.emailSignatures.filter((post) => this.isEmailSignatureTabActive(post))[0] || null;
    },
    selectedEmailSignatureId() {
      if (!this.localEmailSignaturesTab) return null;
      return idFromTabValue('email_signature', this.localEmailSignaturesTab);
    },
    selectedForumSignature() {
      return this.forumSignatures.filter((post) => this.isForumSignatureTabActive(post))[0] || null;
    },
    selectedForumSignatureId() {
      if (!this.localForumSignaturesTab) return null;
      return idFromTabValue('forum_signature', this.localForumSignaturesTab);
    },
    signaturesErrorTabs() {
      let signaturesErrorTabs = [];

      if (this.$v.localFunnelResource.signatures.email.$error) {
        signaturesErrorTabs.push('email');
      }

      if (this.$v.localFunnelResource.signatures.forum.$error) {
        signaturesErrorTabs.push('forum');
      }

      return signaturesErrorTabs;
    },
    emailSignaturesErrorIds() {
      let emailSignaturesErrorIds = [];

      let index;
      for (index in this.$v.localFunnelResource.signatures.email.$each.$iter) {
        if (this.$v.localFunnelResource.signatures.email.$each.$iter[index].$error) {
          emailSignaturesErrorIds.push(
            this.$v.localFunnelResource.signatures.email.$each.$iter[index].$model.id
          );
        }
      }

      return emailSignaturesErrorIds;
    },
    forumSignaturesErrorIds() {
      let forumSignaturesErrorIds = [];

      let index;
      for (index in this.$v.localFunnelResource.signatures.forum.$each.$iter) {
        if (this.$v.localFunnelResource.signatures.forum.$each.$iter[index].$error) {
          forumSignaturesErrorIds.push(
            this.$v.localFunnelResource.signatures.forum.$each.$iter[index].$model.id
          );
        }
      }

      return forumSignaturesErrorIds;
    },
  },
  mounted() {
    this.$root.$on('trigger-validation', () => {
      this.$v.$touch();
    });
  },
  methods: {
    characterCount(value) {
      const length = (value || '').length;
      return `${length} ${length === 1 ? 'character' : 'characters'}`;
    },
    affiliateLinksActionsEmailSignature(index) {
      let affiliateLinksActions = [];

      if (this.landingPages.length > 0) {
        for (let i = 0; i < this.landingPages.length; i++) {
          affiliateLinksActions.push({
            text: this.landingPages[i].description,
            callback: () => this.addAffiliateLinkToEmailSignature(index, this.landingPages[i].id),
          });
        }
      }

      return affiliateLinksActions;
    },
    emailSignatureInsertActions(index) {
      return [
        {
          text: this.$t('frontend_resources_add_affiliate_name'),
          callback: () => this.addAffiliateNameToEmailSignature(index),
        },
        ...this.affiliateLinksActionsEmailSignature(index),
      ];
    },
    affiliateLinksActionsForumSignature(index) {
      let affiliateLinksActions = [];

      if (this.landingPages.length > 0) {
        for (let i = 0; i < this.landingPages.length; i++) {
          affiliateLinksActions.push({
            text: this.landingPages[i].description,
            callback: () => this.addAffiliateLinkToForumSignature(index, this.landingPages[i].id),
          });
        }
      }

      return affiliateLinksActions;
    },
    forumSignatureInsertActions(index) {
      return [
        {
          text: this.$t('frontend_resources_add_affiliate_name'),
          callback: () => this.addAffiliateNameToForumSignature(index),
        },
        ...this.affiliateLinksActionsForumSignature(index),
      ];
    },
    updateFunnelResource() {
      this.$emit('updateFunnelResource', this.localFunnelResource);
    },
    selectEmailSignature(id) {
      this.handleEmailSignaturesTabChange(`email_signature_tab_${id}`);
    },
    selectForumSignature(id) {
      this.handleForumSignaturesTabChange(`forum_signature_tab_${id}`);
    },
    // `AssetItemList` reports the new order as ids; the existing handlers speak
    // the tab shape gd-tabs used, so the ids are wrapped back into it.
    reorderEmailSignatures(ids) {
      this.handleEmailSignaturesTabOrderUpdate(toTabOrder('email_signature', ids));
    },
    reorderForumSignatures(ids) {
      this.handleForumSignaturesTabOrderUpdate(toTabOrder('forum_signature', ids));
    },
    handleEmailSignaturesTabChange(newTab) {
      this.localEmailSignaturesTab = newTab || this.localEmailSignaturesTab;
      this.$emit('handleEmailSignaturesTabChange', this.localEmailSignaturesTab);
    },
    handleForumSignaturesTabChange(newTab) {
      this.localForumSignaturesTab = newTab || this.localForumSignaturesTab;
      this.$emit('handleForumSignaturesTabChange', this.localForumSignaturesTab);
    },
    handleSignaturesTabChange(newTab) {
      this.signaturesTab = newTab;
    },
    handleEmailSignaturesTabOrderUpdate(updatedTabs) {
      sortByTabOrder(this.localFunnelResource.signatures.email, 'email_signature', updatedTabs);

      this.updateFunnelResource();
    },
    createEmailSignature() {
      const newPostId = this.generateNumber();
      const newPostCount = this.localFunnelResource.signatures.email.length + 1;

      this.localFunnelResource.signatures.email.push({
        id: newPostId,
        name: `${this.$t('frontend_groovesell_tools_affiliate_edit_signature')} ${newPostCount}`,
        post: '',
      });

      this.localEmailSignaturesTab = `email_signature_tab_${newPostId}`;

      this.updateFunnelResource();
      this.handleEmailSignaturesTabChange();
    },
    openConfirmModal(index, actionType) {
      this.itemToDeleteIndex = index;
      this.actionType = actionType;
      this.confirmOpen = true;
    },
    applyAction() {
      switch (this.actionType) {
        case 'deleteEmailSignature':
          this.deleteEmailSignature();
          break;
        case 'deleteForumSignature':
          this.deleteForumSignature();
          break;
      }
    },
    async deleteEmailSignature() {
      this.localFunnelResource.signatures.email.splice(this.itemToDeleteIndex, 1);
      this.localEmailSignaturesTab = '';

      if (this.localFunnelResource.signatures.email.length > 0) {
        let nextOpenIndex = 0;
        if (this.itemToDeleteIndex != 0) {
          nextOpenIndex = this.itemToDeleteIndex - 1;
        }

        const nextOpenId =
          this.localFunnelResource.signatures.email[nextOpenIndex].id;
        this.localEmailSignaturesTab = `email_signature_tab_${nextOpenId}`;
      }

      this.updateFunnelResource();
      this.handleEmailSignaturesTabChange();
    },
    addAffiliateNameToEmailSignature(index) {
      this.localFunnelResource.signatures.email[
        index
      ].post = `${this.localFunnelResource.signatures.email[index].post}##affiliateName##`;

      this.updateFunnelResource();
    },
    addAffiliateLinkToEmailSignature(index, landingPageId) {
      this.localFunnelResource.signatures.email[
        index
      ].post = `${this.localFunnelResource.signatures.email[index].post}##affiliateLink${landingPageId}##`;

      this.updateFunnelResource();
    },
    handleForumSignaturesTabOrderUpdate(updatedTabs) {
      sortByTabOrder(this.localFunnelResource.signatures.forum, 'forum_signature', updatedTabs);

      this.updateFunnelResource();
    },
    createForumSignature() {
      const newPostId = this.generateNumber();
      const newPostCount = this.localFunnelResource.signatures.forum.length + 1;

      this.localFunnelResource.signatures.forum.push({
        id: newPostId,
        name: `${this.$t('frontend_groovesell_tools_affiliate_edit_signature')} ${newPostCount}`,
        post: '',
      });

      this.localForumSignaturesTab = `forum_signature_tab_${newPostId}`;

      this.updateFunnelResource();
      this.handleForumSignaturesTabChange();
    },
    async deleteForumSignature() {
      this.localFunnelResource.signatures.forum.splice(this.itemToDeleteIndex, 1);
      this.localForumSignaturesTab = '';

      if (this.localFunnelResource.signatures.forum.length > 0) {
        let nextOpenIndex = 0;
        if (this.itemToDeleteIndex != 0) {
          nextOpenIndex = this.itemToDeleteIndex - 1;
        }

        const nextOpenId = this.localFunnelResource.signatures.forum[nextOpenIndex].id;
        this.localForumSignaturesTab = `forum_signature_tab_${nextOpenId}`;
      }

      this.updateFunnelResource();
      this.handleForumSignaturesTabChange();
    },
    addAffiliateNameToForumSignature(index) {
      this.localFunnelResource.signatures.forum[
        index
      ].post = `${this.localFunnelResource.signatures.forum[index].post}##affiliateName##`;

      this.updateFunnelResource();
    },
    addAffiliateLinkToForumSignature(index, landingPageId) {
      this.localFunnelResource.signatures.forum[
        index
      ].post = `${this.localFunnelResource.signatures.forum[index].post}##affiliateLink${landingPageId}##`;

      this.updateFunnelResource();
    },
    isEmailSignatureTabActive(post) {
      return this.localEmailSignaturesTab === `email_signature_tab_${post.id}`;
    },
    isForumSignatureTabActive(post) {
      return this.localForumSignaturesTab === `forum_signature_tab_${post.id}`;
    },
  },
  validations: {
    localFunnelResource: {
      signatures: {
        email: {
          $each: {
            name: {
              required,
              minLength: minLength(3),
              maxLength: maxLength(80),
            },
            post: {
              required,
              minLength: minLength(2),
            },
            $trackBy: 'id',
          },
        },
        forum: {
          $each: {
            name: {
              required,
              minLength: minLength(3),
              maxLength: maxLength(80),
            },
            post: {
              required,
              minLength: minLength(2),
            },
            $trackBy: 'id',
          },
        },
      },
    },
  },
};
</script>

<style src="./asset-split.css"></style>
